import { useContext } from "react";
import { UseQueryResult, useQuery } from "@tanstack/react-query";

import { CaseEvent } from "interfaces/case-event";
import { AxiosContext } from "contexts/AxiosContext";
import { PaginatedResponse } from "interfaces/utils";
import { dataOrAll } from "hooks/queries/utils/dataOrAll";
import { makeQueryString } from "core/makeQueryString";
import { dateFormat } from "core/datetime";
import { truncate } from "lodash";
import { getDraftJsText } from "core/utils/string";

export type CaseEventWithOptionLabel = CaseEvent & { option_label: string };

const STALE_TIME = 0;

interface CommonArgs {
  ordering?: string;
  page?: number;
  search?: string;
  size?: number;
  enabled?: boolean;
}

export const useEventQuery = (
  id: number,
  enabled = true
): UseQueryResult<CaseEvent, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  return useQuery({
    queryKey: ["events", id],
    queryFn: async () => {
      const { data } = await axios.get<CaseEvent>(`/events/${id}`);

      return data;
    },
    staleTime: STALE_TIME,
    enabled
  });
};

interface EventsQueryArgs extends CommonArgs {
  case_id__in?: string | number;
  type__in?: string | number;
}

export const useEventsQuery = (
  args: EventsQueryArgs = {}
): UseQueryResult<PaginatedResponse<CaseEventWithOptionLabel[]>, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs);

  return useQuery({
    queryKey: ["events", dataOrAll(args)],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<CaseEvent[]>>(
        `/events${qs}`
      );

      const results = data.results.map(event => {
        const dateTime = dateFormat(event.date_time, {
          format: "Do MMM YYYY"
        } as any);

        const option_label = `${dateTime}: ${truncate(
          getDraftJsText(event.description),
          {
            length: 100,
            omission: "…"
          }
        )}`;

        return {
          ...event,
          option_label
        };
      });

      return { ...data, results };
    },
    staleTime: STALE_TIME,
    enabled
  });
};
